import * as CSS from 'csstype';
import { sizes, Space } from 'projects/libs/core-components/spaces';
import { joinAndFilter } from 'projects/libs/utils/joinAndFilter/joinAndFilter';
import { resolveResponsiveProp, ResponsiveProp } from 'projects/libs/utils/responsiveProps';
import styled, { css } from 'styled-components';

type TLengthSystem = string | number | 0;

export interface BoxProps {
    // Display
    display?: CSS.DisplayProperty;

    maxHeight?: CSS.MaxHeightProperty<any>;

    // Spaces
    margin?: ResponsiveProp<Space>;
    marginX?: ResponsiveProp<Space>;
    marginY?: ResponsiveProp<Space>;
    marginBottom?: ResponsiveProp<Space>;
    marginTop?: ResponsiveProp<Space>;
    marginLeft?: ResponsiveProp<Space>;
    marginRight?: ResponsiveProp<Space>;

    padding?: ResponsiveProp<Space>;
    paddingX?: ResponsiveProp<Space>;
    paddingY?: ResponsiveProp<Space>;
    paddingBottom?: ResponsiveProp<Space>;
    paddingTop?: ResponsiveProp<Space>;
    paddingLeft?: ResponsiveProp<Space>;
    paddingRight?: ResponsiveProp<Space>;

    // Flexbox
    flexDirection?: CSS.FlexDirectionProperty;
    flexWrap?: CSS.FlexWrapProperty;
    justifyContent?: CSS.JustifyContentProperty;
    alignItems?: CSS.AlignItemsProperty;
    alignContent?: CSS.AlignContentProperty;
    order?: CSS.GlobalsNumber;
    flex?: CSS.FlexProperty<TLengthSystem>;
    flexGrow?: CSS.GlobalsNumber;
    flexShrink?: CSS.GlobalsNumber;
    alignSelf?: CSS.AlignSelfProperty;
}

const displayStyle = (display: CSS.DisplayProperty) =>
    css`
        display: ${display};
    `;

const maxHeightStyle = (maxHeight: CSS.MaxHeightProperty<any>) =>
    css`
        max-height: ${maxHeight};
    `;

const flexDirectionStyle = (flexDirection: CSS.FlexDirectionProperty) => css`
    flex-direction: ${flexDirection};
`;

const flexWrapStyle = (flexWrap: CSS.FlexWrapProperty) => css`
    flex-wrap: ${flexWrap};
`;

const justifyContentStyle = (justifyContent: CSS.JustifyContentProperty) => css`
    justify-content: ${justifyContent};
`;

const alignItemsStyle = (alignItems: CSS.AlignItemsProperty) => css`
    align-items: ${alignItems};
`;

const alignContentStyle = (alignContent: CSS.AlignContentProperty) => css`
    align-content: ${alignContent};
`;

const orderStyle = (order: CSS.GlobalsNumber) => css`
    order: ${order};
`;

const flexStyle = (flex: CSS.FlexProperty<TLengthSystem>) => css`
    flex: ${flex};
`;

const flexGrowStyle = (flexGrow: CSS.GlobalsNumber) => css`
    flex-grow: ${flexGrow};
`;

const flexShrinkStyle = (flexShrink: CSS.GlobalsNumber) => css`
    flex-shrink: ${flexShrink};
`;

const alignSelfStyle = (alignSelf: CSS.AlignSelfProperty) => css`
    align-self: ${alignSelf};
`;

const Box = styled.div<BoxProps>`
    ${({ display }) => display && displayStyle(display)}

    ${({ maxHeight }) => maxHeight && maxHeightStyle(maxHeight)}

    ${({ margin, marginX, marginY, marginBottom, marginTop, marginLeft, marginRight }) => {
        const resolvedMarginTop = marginTop || marginY || margin;
        const resolvedMarginBottom = marginBottom || marginY || margin;
        const resolvedMarginLeft = marginLeft || marginX || margin;
        const resolvedMarginRight = marginRight || marginX || margin;

        return joinAndFilter(
            resolvedMarginTop && resolveResponsiveProp(resolvedMarginTop, 'margin-top', sizes),
            resolvedMarginBottom &&
                resolveResponsiveProp(resolvedMarginBottom, 'margin-bottom', sizes),
            resolvedMarginLeft && resolveResponsiveProp(resolvedMarginLeft, 'margin-left', sizes),
            resolvedMarginRight && resolveResponsiveProp(resolvedMarginRight, 'margin-right', sizes)
        );
    }}

    ${({ padding, paddingX, paddingY, paddingBottom, paddingTop, paddingLeft, paddingRight }) => {
        const resolvedPaddingTop = paddingTop || paddingY || padding;
        const resolvedPaddingBottom = paddingBottom || paddingY || padding;
        const resolvedPaddingLeft = paddingLeft || paddingX || padding;
        const resolvedPaddingRight = paddingRight || paddingX || padding;

        return joinAndFilter(
            resolvedPaddingTop && resolveResponsiveProp(resolvedPaddingTop, 'padding-top', sizes),
            resolvedPaddingBottom &&
                resolveResponsiveProp(resolvedPaddingBottom, 'padding-bottom', sizes),
            resolvedPaddingLeft &&
                resolveResponsiveProp(resolvedPaddingLeft, 'padding-left', sizes),
            resolvedPaddingRight &&
                resolveResponsiveProp(resolvedPaddingRight, 'padding-right', sizes)
        );
    }}

    ${({ flexDirection }) => flexDirection && flexDirectionStyle(flexDirection)}
    ${({ flexWrap }) => flexWrap && flexWrapStyle(flexWrap)}
    ${({ justifyContent }) => justifyContent && justifyContentStyle(justifyContent)}
    ${({ alignItems }) => alignItems && alignItemsStyle(alignItems)}
    ${({ alignContent }) => alignContent && alignContentStyle(alignContent)}
    ${({ order }) => order && orderStyle(order)}
    ${({ flex }) => flex && flexStyle(flex)}
    ${({ flexGrow }) => flexGrow && flexGrowStyle(flexGrow)}
    ${({ flexShrink }) => flexShrink && flexShrinkStyle(flexShrink)}
    ${({ alignSelf }) => alignSelf && alignSelfStyle(alignSelf)}
`;

export default Box;
